// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Measures the rack elements that are not conduits — struts, trapezes, end boxes. A support supplies the
 * reference edges a chain closes on, so only its extent in the rack's frame matters, never its kind.
 */

import {
  projectIntoRackFrame,
  TIER_TOLERANCE_FT,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/rackFrame';
import { enumFragmentVertices } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/geometryHelpers';
import type {
  ProjectedRackPipe,
  RackFrame,
  RackFrameRange,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/types';
import { type AutodeskFragmentList, getElementFragmentIds } from 'components/Autodesk/extensions/viewerHelpers';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { isNil } from 'helpers/isNotNil';

/** How far a conduit may stop short of a support and still count as running through it. */
const SUPPORT_END_TOLERANCE_FT = 0.25;
/** How far outside a support's own height a row may sit and still be the row it carries. */
const SUPPORT_HEIGHT_REACH_FT = TIER_TOLERANCE_FT * 3;

/**
 * The extent of a non-conduit rack element — a strut, trapeze, or end box — in its rack's frame. Supports
 * provide the reference edges each row's chain closes on, and are never classified by kind: what a support
 * carries comes from its measured height span.
 */
export type RackSupportBounds = {
  dbId: AutodeskDbId;
  minWidthFt: number;
  maxWidthFt: number;
  minHeightFt: number;
  maxHeightFt: number;
  minStationFt: number;
  maxStationFt: number;
};

/** A support's measured extent in the rack's frame, before the element it came from is attached. */
type SupportExtent = Omit<RackSupportBounds, 'dbId'>;

const rangesOverlap = (range: RackFrameRange, other: RackFrameRange, toleranceFt = 0): boolean =>
  range.minFt - toleranceFt <= other.maxFt && other.minFt - toleranceFt <= range.maxFt;

/** A support's extent in the rack's frame, or `null` when it has no solid geometry. */
const measureSupportInFrame = (
  instanceTree: Autodesk.Viewing.InstanceTree,
  fragList: AutodeskFragmentList,
  dbId: AutodeskDbId,
  frame: RackFrame,
): SupportExtent | null => {
  // Accumulated in place so a support's many thousands of vertices are never materialized into an array.
  const extent: SupportExtent = {
    minWidthFt: Infinity,
    maxWidthFt: -Infinity,
    minHeightFt: Infinity,
    maxHeightFt: -Infinity,
    minStationFt: Infinity,
    maxStationFt: -Infinity,
  };

  // Real mesh vertices, since an axis-aligned box around an angled strut is wider than the strut, and the
  // excess would be reported as rack layout.
  const accumulate = (worldPoint: THREE.Vector3) => {
    const { widthFt, heightFt, stationFt } = projectIntoRackFrame(worldPoint, frame);
    extent.minWidthFt = Math.min(extent.minWidthFt, widthFt);
    extent.maxWidthFt = Math.max(extent.maxWidthFt, widthFt);
    extent.minHeightFt = Math.min(extent.minHeightFt, heightFt);
    extent.maxHeightFt = Math.max(extent.maxHeightFt, heightFt);
    extent.minStationFt = Math.min(extent.minStationFt, stationFt);
    extent.maxStationFt = Math.max(extent.maxStationFt, stationFt);
  };

  for (const fragId of getElementFragmentIds(instanceTree, dbId)) {
    const geometry = fragList.getGeometry?.(fragId);
    if (geometry?.isLines === true) continue;
    enumFragmentVertices(fragList, fragId, accumulate);
  }

  return Number.isFinite(extent.minWidthFt) ? extent : null;
};

/** Measure one support in the frame of the conduits it carries, or `null` when it has no solid geometry. */
export const measureSupport = (
  instanceTree: Autodesk.Viewing.InstanceTree,
  fragList: AutodeskFragmentList,
  dbId: AutodeskDbId,
  frame: RackFrame,
): RackSupportBounds | null => {
  const bounds = measureSupportInFrame(instanceTree, fragList, dbId, frame);
  return isNil(bounds) ? null : { dbId, ...bounds };
};

/** The conduits a support reaches across — the only ones its edges can dimension. */
export const pipesSpannedBy = (support: RackSupportBounds, pipes: ProjectedRackPipe[]): ProjectedRackPipe[] =>
  pipes.filter(({ widthFt }) => widthFt >= support.minWidthFt && widthFt <= support.maxWidthFt);

/**
 * The conduits that run through the support, rather than merely passing near it. Candidates are gathered by
 * bounding-box proximity, so they include conduit that stops short of it — a second rack passing below, a
 * run terminating just before — and conduit absent from this station is not on the rack it carries.
 */
export const pipesCrossingSupport = (support: RackSupportBounds, pipes: ProjectedRackPipe[]): ProjectedRackPipe[] =>
  pipes.filter(({ startStationFt, endStationFt }) =>
    rangesOverlap(
      { minFt: Math.min(startStationFt, endStationFt), maxFt: Math.max(startStationFt, endStationFt) },
      { minFt: support.minStationFt, maxFt: support.maxStationFt },
      SUPPORT_END_TOLERANCE_FT,
    ),
  );

/**
 * True when a support crosses the rack rather than running alongside it — wider across the rack than it is
 * deep along the run. Rejects a rail, whose faces sit at the ends of the run instead of beside the conduits,
 * so a chain drawn on one lands in empty space.
 */
const crossesTheRack = (support: RackSupportBounds): boolean =>
  support.maxWidthFt - support.minWidthFt > support.maxStationFt - support.minStationFt;

/**
 * True when a support spans the rack, which is what makes it worth dimensioning: it crosses the rack, and
 * reaches across 2+ of the conduits running through it.
 *
 * A chain also needs a reference edge outside each end of a row, but only a row can answer that — a strut
 * narrower than the row above it still dimensions the row it holds. `measureTier` asks it per row.
 */
export const isSpanningSupport = (support: RackSupportBounds, pipes: ProjectedRackPipe[]): boolean =>
  crossesTheRack(support) && pipesSpannedBy(support, pipes).length >= 2;

/**
 * True when this support carries a given row: the row sits within the support's whole vertical span, give or
 * take the standoff between a centerline and the metal. The *whole* span, because conduit rests on a strut,
 * hangs under one on clamps, and sits between the rails of a trapeze — so a trapeze tall enough for two
 * rows carries both.
 */
export const supportServesTier = (support: RackSupportBounds, referenceHeightFt: number): boolean =>
  referenceHeightFt >= support.minHeightFt - SUPPORT_HEIGHT_REACH_FT &&
  referenceHeightFt <= support.maxHeightFt + SUPPORT_HEIGHT_REACH_FT;
